// CORE-496 — home-page onboarding tour: state + anchor registry + orchestration.
//
// CORE-1750 — тур идёт и в приложении (решение Рината 19.10), с тем же
// серверным состоянием: прошёл на одной платформе — на другой не предлагается,
// потому что решение об автозапуске одно и принимает его сервер. Веб-only
// остались подсветка блоков (OnboardingTourOverlay сам не рисуется вне веба) и
// ручной повтор (`replay`, кнопка TourReplayBanner).
//
// CORE-1750 (возврат QA) — провайдер держит и историю тура с сервера
// (`history`): по ней кнопка повтора решает, звать ли в тур крупно или только
// тихой ссылкой. Своего хранилища у истории нет — она из ответов
// /api/onboarding/tour, и перезагрузка или другое устройство читают её заново.
//
// The user-facing surface is Agapella's own pop-up (AgapellaTourPanel inside
// AgapellaWidget; in the app — inside her sheet) — she speaks every step
// herself; on the web the spotlight overlay only dims the page and rings the
// block under discussion. Both are dumb; this provider owns:
//   • server state sync (api.getOnboardingTour + start/step/skip/close/complete)
//   • auto-launch exactly once after registration (shouldAutoLaunch), and only
//     on the home route — elsewhere it waits for Home (CORE-1435)
//   • pausing a running tour on Live entry routes, resuming it on return (CORE-1435)
//   • an anchor registry so home blocks can expose their DOM node to spotlight
//   • step progression + the brief "skip step" transition copy
//   • the farewell line left in the pop-up before it collapses into the icon
//   • mirroring every line into the Agapella chat history
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Platform } from 'react-native';

import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { navigationRef } from '../navigation/rootNavigation';
import { useCurrentRouteName } from '../widget/useCurrentRouteName';
import { ONBOARDING_TOUR_ENABLED } from '../config/flags';
import { DEFAULT_ASSISTANT_ID } from '../constants/chat';
import { emitProductEvent, ProductEvents } from '../services/productEvents';
import { logTourMessage, TourTranscriptKind } from '../services/onboardingTourTranscript';
import {
  TOUR_FAREWELL_CLOSED,
  TOUR_FAREWELL_SKIPPED,
  TOUR_STEPS,
  TOTAL_TOUR_STEPS,
  TourAnchorKey,
  TourStep,
  getTourStep,
  nextStepId,
  stepTranscript,
} from './tourSteps';
import { canAutoLaunchTourOn, isTourPausedOn } from './tourRoutes';
import { autoLaunchTarget, AutoLaunchTarget } from './tourAutoLaunch';
import { closeIntent } from './tourActions';
import { nextTourHistory, TourHistory } from './tourReplayEntry';
import { OnboardingTourDto } from '../types/onboardingTour';

const IS_WEB = Platform.OS === 'web';

/**
 * CORE-1750 — платформа в событиях тура (`web` / `ios` / `android`): тур идёт и
 * на сайте, и в приложении, а состояние у них общее, так что различить их
 * аналитика может только по событию.
 */
const PLATFORM = Platform.OS;

/** How long Agapella's farewell stays in the pop-up before it collapses. */
const FAREWELL_MS = 6000;

/** Grace period after the auto-launch decision, so the home layout (and its
 *  spotlight anchors) have mounted before she starts talking about them. */
const AUTO_LAUNCH_DELAY_MS = 900;

type AnchorNode = unknown; // DOM element on web; unused on native.

interface OnboardingTourContextValue {
  /** True while the overlay should be shown. */
  isActive: boolean;
  /** The step currently displayed, or null when inactive. */
  step: TourStep | null;
  totalSteps: number;
  /** Non-null while a "skip step" transition message is showing. */
  transitionText: string | null;
  /**
   * Agapella's parting line after a skip/close. Non-null for a few seconds
   * AFTER `isActive` goes false — the pop-up shows it, then collapses into the
   * launcher icon.
   */
  farewell: string | null;
  /** Dismiss the farewell early (user closed the pop-up). */
  dismissFarewell: () => void;
  /**
   * CORE-1750 — завершал ли или пропускал человек тур хоть раз, по ответам
   * сервера. Решает, какой вход в повтор стоит в чате (см. tourReplayEntry).
   */
  history: TourHistory;
  /** Register a home block so the overlay can spotlight it. */
  registerAnchor: (key: TourAnchorKey) => (node: AnchorNode | null) => void;
  /** Current DOM node for the active step's anchor (web), or null. */
  getActiveAnchorNode: () => AnchorNode | null;
  // Actions
  start: () => void;
  replay: () => void;
  next: () => void;
  complete: () => void;
  skipStep: () => void;
  skipAll: () => void;
  close: () => void;
  goToChat: () => void;
}

const OnboardingTourContext = createContext<OnboardingTourContextValue | null>(null);

export const OnboardingTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  const [isActive, setIsActive] = useState(false);
  const [stepId, setStepId] = useState(1);
  const [transitionText, setTransitionText] = useState<string | null>(null);
  const [farewell, setFarewell] = useState<string | null>(null);
  const [history, setHistory] = useState<TourHistory>('loading');
  // Номер входа в аккаунт: ответ, приехавший после выхода, — про прежнего
  // человека, и его историю следующему приписывать нельзя.
  const session = useRef(0);

  const anchors = useRef(new Map<TourAnchorKey, AnchorNode | null>());
  const autoLaunchTried = useRef(false);
  const transitionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const farewellTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoLaunchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // CORE-1435 — сервер разрешил автозапуск, а тур ещё не открывался. Флаг живёт,
  // пока человек не дойдёт до главной: вход по ссылке на Live откладывает
  // знакомство, а не отменяет его.
  const [autoLaunchPending, setAutoLaunchPending] = useState(false);
  // CORE-1447 — с какого шага открывать. Отложенный тур возвращается туда, где
  // человек остановился: это ровно то, что Агапелла обещала ему на прощание.
  const autoLaunchFrom = useRef<AutoLaunchTarget>({ kind: 'start' });
  const channelKey = useRef<string | null>(null);

  // Маршрут читается и в таймере автозапуска — к моменту срабатывания человек
  // мог уйти с главной, поэтому нужен свежий, а не пойманный замыканием.
  const routeName = useCurrentRouteName();
  const routeNameRef = useRef(routeName);
  routeNameRef.current = routeName;

  const step = isActive ? getTourStep(stepId) ?? null : null;

  // Read through a ref, not a dependency. `isAuthenticated` flips before the
  // /api/auth/me round-trip lands, so a userId dependency would re-identify
  // start() mid-flight: the auto-launch effect below would re-run, its cleanup
  // would cancel the pending launch, and the `autoLaunchTried` latch would stop
  // it re-arming — the tour would silently never open. Keeping these callbacks
  // stable also keeps them from closing over an empty id.
  const userIdRef = useRef('');
  userIdRef.current = user?.id ? String(user.id) : '';

  // Mirror one of Agapella's tour lines into the shared chat history. The
  // companion channel is provisioned on demand (idempotent) and cached for the
  // rest of the session. Fire-and-forget — the transcript never gates the tour.
  const logToChat = useCallback((text: string, kind: TourTranscriptKind, forStep?: number) => {
    const userId = userIdRef.current;
    if (!userId || !text) return;
    const post = (key: string) =>
      logTourMessage({ userId, channelKey: key, text, kind, step: forStep });
    if (channelKey.current) {
      void post(channelKey.current);
      return;
    }
    void api
      .ensureAiCompanionChannel(DEFAULT_ASSISTANT_ID)
      .then(({ channelKey: key }) => {
        channelKey.current = key;
        return post(key);
      })
      .catch(() => {
        /* no channel → no transcript line; the tour itself is unaffected */
      });
  }, []);

  /** Announce a step: show it and mirror it into the chat. */
  const announce = useCallback(
    (id: number) => {
      const s = getTourStep(id);
      if (!s) return;
      logToChat(stepTranscript(s), 'step', id);
      emitProductEvent(ProductEvents.onboardingTourStep, {
        userId: userIdRef.current,
        props: { step: id, platform: PLATFORM },
      });
    },
    [logToChat],
  );

  const registerAnchor = useCallback(
    (key: TourAnchorKey) => (node: AnchorNode | null) => {
      anchors.current.set(key, node);
    },
    [],
  );

  const getActiveAnchorNode = useCallback(() => {
    if (!step?.anchor) return null;
    return anchors.current.get(step.anchor) ?? null;
  }, [step]);

  // CORE-1750 — каждый переход тура сервер подтверждает своим состоянием; из
  // него и берём историю, а не выводим её сами из того, что нажал человек.
  const adoptHistory = useCallback((state: OnboardingTourDto, at: number) => {
    if (at !== session.current) return;
    setHistory((prev) => nextTourHistory(prev, state));
  }, []);

  // Fire-and-forget server sync; the UI never blocks on it.
  const persist = useCallback((fn: () => Promise<OnboardingTourDto>) => {
    const at = session.current;
    fn()
      .then((state) => adoptHistory(state, at))
      .catch(() => {
        /* analytics/UX tolerate a lost tour write — reconciled on next load */
      });
  }, [adoptHistory]);

  const dismissFarewell = useCallback(() => {
    if (farewellTimer.current) {
      clearTimeout(farewellTimer.current);
      farewellTimer.current = null;
    }
    setFarewell(null);
  }, []);

  /** Leave Agapella's parting line in the pop-up, then collapse to the icon. */
  const sayFarewell = useCallback(
    (text: string, kind: TourTranscriptKind) => {
      logToChat(text, kind, stepId);
      setFarewell(text);
      if (farewellTimer.current) clearTimeout(farewellTimer.current);
      farewellTimer.current = setTimeout(() => setFarewell(null), FAREWELL_MS);
    },
    [logToChat, stepId],
  );

  // CORE-1435 — любой запуск тура (сам или «Пройти знакомство» из чата)
  // исчерпывает отложенный автозапуск. Иначе повтор из чата уводит на главную,
  // там же взводится ожидавший автозапуск, и тур открывается второй раз поверх
  // первого — с повтором реплики в истории чата.
  const cancelPendingAutoLaunch = useCallback(() => {
    if (autoLaunchTimer.current) {
      clearTimeout(autoLaunchTimer.current);
      autoLaunchTimer.current = null;
    }
    setAutoLaunchPending(false);
  }, []);

  const openAt = useCallback(
    (id: number) => {
      dismissFarewell();
      cancelPendingAutoLaunch();
      setStepId(id);
      setTransitionText(null);
      setIsActive(true);
      announce(id);
    },
    [announce, dismissFarewell, cancelPendingAutoLaunch],
  );

  const start = useCallback(() => {
    openAt(1);
    persist(() => api.startOnboardingTour());
  }, [openAt, persist]);

  // CORE-1750 — ручной повтор остаётся только на сайте: в приложение кнопку
  // повтора не добавляем (решение 19.10), и повтору там неоткуда прийти.
  const replay = useCallback(() => {
    if (!IS_WEB) return;
    openAt(1);
    persist(() => api.replayOnboardingTour());
  }, [openAt, persist]);

  // CORE-1447 — продолжить отложенный тур с сохранённого шага. Отдельно от
  // `start`, потому что «начать» сбрасывает прогресс на первый шаг и считает
  // новый запуск, а здесь не новый запуск, а возвращение в тот же.
  const resume = useCallback(
    (step: number) => {
      openAt(step);
      persist(() => api.onboardingTourStep(step));
    },
    [openAt, persist],
  );

  const finish = useCallback(() => {
    // Every exit (complete / skip-all / close) goes through here, so this is
    // where a pending "skip step" advance gets cancelled. Without it, closing
    // during the transition still fires advanceTo() a moment later — writing a
    // step the user never saw to both the server and the chat transcript.
    if (transitionTimer.current) {
      clearTimeout(transitionTimer.current);
      transitionTimer.current = null;
    }
    setIsActive(false);
    setTransitionText(null);
  }, []);

  const complete = useCallback(() => {
    finish();
    // CORE-1750 — завершение сервер записывает всегда (счётчик завершений), и
    // ждать его ответа незачем: «Перейти в чат» открывает полный чат сразу, и
    // приглашение пройти тур мигнуло бы над ним до ответа.
    setHistory('finished');
    persist(() => api.completeOnboardingTour());
    emitProductEvent(ProductEvents.onboardingTourCompleted, {
      userId: userIdRef.current,
      props: { platform: PLATFORM },
    });
  }, [finish, persist]);

  const advanceTo = useCallback(
    (id: number) => {
      const next = getTourStep(id);
      if (!next) {
        complete();
        return;
      }
      setStepId(id);
      setTransitionText(null);
      announce(id);
      persist(() => api.onboardingTourStep(id, false));
    },
    [announce, complete, persist],
  );

  const next = useCallback(() => {
    if (!step) return;
    if (step.primaryAction === 'complete' || step.id >= TOTAL_TOUR_STEPS) {
      complete();
      return;
    }
    if (step.primaryAction === 'chat') {
      // handled by goToChat; keep next() safe
      complete();
      return;
    }
    const nid = nextStepId(step.id);
    if (nid == null) complete();
    else advanceTo(nid);
  }, [step, advanceTo, complete]);

  const skipStep = useCallback(() => {
    if (!step) return;
    persist(() => api.onboardingTourStep(step.id, true));
    const nid = nextStepId(step.id);
    if (nid == null) {
      complete();
      return;
    }
    // Show the brief "skip" transition copy, then advance.
    if (step.skipBody) {
      setTransitionText(step.skipBody);
      logToChat(step.skipBody, 'skip_step', step.id);
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
      transitionTimer.current = setTimeout(() => advanceTo(nid), 2600);
    } else {
      advanceTo(nid);
    }
  }, [step, persist, complete, advanceTo, logToChat]);

  const skipAll = useCallback(() => {
    finish();
    // Как у завершения: пропуск сервер тоже считает всегда.
    setHistory('finished');
    persist(() => api.skipOnboardingTour());
    emitProductEvent(ProductEvents.onboardingTourSkipped, {
      userId: userIdRef.current,
      props: { step: stepId, scope: 'all', platform: PLATFORM },
    });
    sayFarewell(TOUR_FAREWELL_SKIPPED, 'skip_all');
  }, [finish, persist, sayFarewell, stepId]);

  const close = useCallback(() => {
    const current = stepId;
    // CORE-1750 — крестик на последнем шаге завершает тур, а не откладывает:
    // благодарность за знакомство уже прозвучала, и прощание «Я запомнила, где
    // мы остановились» сказало бы обратное. Сервер с той же задачи и сам
    // пишет такое закрытие завершением, но фронт не полагается на это.
    if (closeIntent(current) === 'complete') {
      complete();
      return;
    }
    finish();
    persist(() => api.closeOnboardingTour(current));
    emitProductEvent(ProductEvents.onboardingTourSkipped, {
      userId: userIdRef.current,
      props: { step: current, scope: 'closed', platform: PLATFORM },
    });
    sayFarewell(TOUR_FAREWELL_CLOSED, 'close');
  }, [stepId, complete, finish, persist, sayFarewell]);

  // Step 5 → «Чаты → Agapella». Naming her via `companionId` matters: plain
  // navigation to `Chats` only auto-selects the assistant on DESKTOP, so on a
  // narrow viewport the user landed on the conversation list and never reached
  // her — which reads as "the button doesn't open the chat".
  const goToChat = useCallback(() => {
    complete();
    if (navigationRef.isReady?.()) {
      (navigationRef.navigate as unknown as (name: string, params?: object) => void)(
        'Chats', { companionId: DEFAULT_ASSISTANT_ID },
      );
    }
  }, [complete]);

  // `start` is stable, but keep it behind a ref so the auto-launch effect below
  // never has to list it as a dependency.
  const startRef = useRef(start);
  startRef.current = start;
  const resumeRef = useRef(resume);
  resumeRef.current = resume;

  // Auto-launch once, after registration, for authed users — on the web and,
  // since CORE-1750, in the app (there the tour opens Agapella's sheet).
  // Gated off by ONBOARDING_TOUR_ENABLED (the provider stays mounted so anchor
  // registration keeps working, but the tour never starts and no API call fires).
  //
  // The pending launch deliberately survives re-renders: it is held in a ref and
  // only cleared on unmount/logout, never by this effect's cleanup. Cancelling it
  // from cleanup means any dependency re-emitting inside the delay window kills
  // the launch — and the `autoLaunchTried` latch then stops it re-arming, so the
  // user silently never sees the onboarding.
  //
  // CORE-1435 — решение разделено на два шага. Здесь, как и раньше, один раз
  // спрашиваем сервер; ниже — ждём главную и только там взводим таймер.
  useEffect(() => {
    if (!ONBOARDING_TOUR_ENABLED || !isAuthenticated || autoLaunchTried.current) return;
    autoLaunchTried.current = true;
    const at = session.current;
    api
      .getOnboardingTour()
      .then((state) => {
        // Ответ пришёл после выхода из аккаунта (защёлка уже сброшена) — он
        // про прежнего человека, откладывать по нему нечего.
        if (!autoLaunchTried.current) return;
        adoptHistory(state, at);
        const target = autoLaunchTarget(state);
        if (!target) return;
        autoLaunchFrom.current = target;
        setAutoLaunchPending(true);
      })
      .catch(() => {
        /* absent/failed state → no auto-launch; user can replay from chat */
        if (at === session.current) {
          setHistory((prev) => (prev === 'loading' ? 'unavailable' : prev));
        }
      });
  }, [isAuthenticated, adoptHistory]);

  // CORE-1435 — отложенный автозапуск срабатывает только на главной: якоря тура
  // живут там, а на любом другом экране его затемнение закрывает чужие кнопки
  // (на телефоне — «Присоединиться» на странице Live).
  //
  // Правило из комментария выше соблюдено: у эффекта нет очистки, взведённый
  // таймер не снимается сменой зависимостей. Если за паузу человек ушёл с
  // главной, таймер сам ничего не запускает, флаг остаётся — и следующий заход
  // на главную взводит таймер заново.
  useEffect(() => {
    if (!autoLaunchPending || !isAuthenticated || autoLaunchTimer.current) return;
    if (!canAutoLaunchTourOn(routeName)) return;
    // Small delay so the home layout (and anchors) mount first.
    autoLaunchTimer.current = setTimeout(() => {
      autoLaunchTimer.current = null;
      if (!canAutoLaunchTourOn(routeNameRef.current)) return;
      const target = autoLaunchFrom.current;
      if (target.kind === 'resume') {
        resumeRef.current(target.step);
      } else {
        startRef.current();
      }
    }, AUTO_LAUNCH_DELAY_MS);
  }, [autoLaunchPending, isAuthenticated, routeName]);

  // Reset the "tried" latch on logout so a different user can auto-launch, and
  // drop the cached channel key — it is per-user.
  useEffect(() => {
    if (!isAuthenticated) {
      autoLaunchTried.current = false;
      channelKey.current = null;
      session.current += 1;
      setHistory('loading');
      if (autoLaunchTimer.current) {
        clearTimeout(autoLaunchTimer.current);
        autoLaunchTimer.current = null;
      }
      setAutoLaunchPending(false);
      autoLaunchFrom.current = { kind: 'start' };
      setIsActive(false);
      setFarewell(null);
    }
  }, [isAuthenticated]);

  useEffect(
    () => () => {
      if (transitionTimer.current) clearTimeout(transitionTimer.current);
      if (farewellTimer.current) clearTimeout(farewellTimer.current);
      if (autoLaunchTimer.current) clearTimeout(autoLaunchTimer.current);
    },
    [],
  );

  // CORE-1435 — на входе в Live идущий тур стоит на паузе: наружу он «не идёт»,
  // поэтому оверлей не затемняет страницу, а поп-ап Agapella сворачивается в
  // значок и не закрывает на телефоне кнопку входа. Внутреннее состояние
  // (isActive, stepId) не трогаем — на главной тур продолжается с того же шага,
  // без новой реплики в истории чата.
  const shown = isActive && !isTourPausedOn(routeName);

  const value = useMemo<OnboardingTourContextValue>(
    () => ({
      isActive: shown,
      step: shown ? step : null,
      totalSteps: TOTAL_TOUR_STEPS,
      transitionText: shown ? transitionText : null,
      farewell,
      dismissFarewell,
      history,
      registerAnchor,
      getActiveAnchorNode,
      start,
      replay,
      next,
      complete,
      skipStep,
      skipAll,
      close,
      goToChat,
    }),
    [
      shown,
      step,
      transitionText,
      farewell,
      dismissFarewell,
      history,
      registerAnchor,
      getActiveAnchorNode,
      start,
      replay,
      next,
      complete,
      skipStep,
      skipAll,
      close,
      goToChat,
    ],
  );

  return (
    <OnboardingTourContext.Provider value={value}>{children}</OnboardingTourContext.Provider>
  );
};

/** Access the tour controller. Returns null outside the provider. */
export function useOnboardingTour(): OnboardingTourContextValue | null {
  return useContext(OnboardingTourContext);
}

/**
 * Attach a home block as a tour spotlight target. Returns a ref callback to put
 * on a View (`ref={useTourAnchor('masters')}`). No-op outside the provider.
 */
export function useTourAnchor(key: TourAnchorKey): (node: AnchorNode | null) => void {
  const ctx = useContext(OnboardingTourContext);
  const noop = useRef((_node: AnchorNode | null) => {}).current;
  return ctx ? ctx.registerAnchor(key) : noop;
}

export const __TOUR_STEPS_FOR_TEST = TOUR_STEPS;
